import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { supaFetch } from '../../lib/supabase.js';

// ─── useMyOrders ──────────────────────────────────────────────────────────────
// Fetches the retailer's own order history via get_my_orders SECURITY DEFINER RPC.
// Uses TanStack Query for reliable caching and stale-state management.
// Does NOT require retailerProfile to be resolved first — token alone is enough.
// Returns [] for noprice/guest tokens (no linked retailer_application).
export function useMyOrders(token) {
  return useQuery({
    queryKey:  ['myOrders', token ?? null],
    queryFn:   () =>
      supaFetch('POST', 'rpc/get_my_orders', { p_token: token }, '', { 'Prefer': 'return=representation' })
        .then(({ data }) => Array.isArray(data) ? data : []),
    enabled:   !!token,
    staleTime: 30_000,   // 30s — orders change only when placed/updated
    gcTime:    5 * 60_000,
    select:    (data) => data,
  });
}

// ─── useOrderDetail ───────────────────────────────────────────────────────────
// Lazily fetches the full items JSONB for a single order on demand.
// Uses manual state (not TanStack Query) so we can control exactly when to fetch
// without creating a query per-order on mount.
export function useOrderDetail(token) {
  const [details, setDetails] = useState({}); // { [orderId]: { items, loading } }

  const fetchDetail = async (orderId) => {
    if (!token || !orderId) return;
    if (details[orderId]?.items) return; // already loaded

    setDetails(prev => ({ ...prev, [orderId]: { ...prev[orderId], loading: true } }));
    try {
      const { data } = await supaFetch(
        'POST', 'rpc/get_order_detail',
        { p_token: token, p_order_id: orderId },
        '', { 'Prefer': 'return=representation' }
      );
      const order = Array.isArray(data) ? data[0] : null;
      setDetails(prev => ({
        ...prev,
        [orderId]: {
          items:       order?.items       ?? [],
          notes:       order?.notes,
          admin_notes: order?.admin_notes,
          loading:     false,
        },
      }));
    } catch {
      setDetails(prev => ({ ...prev, [orderId]: { items: [], loading: false } }));
    }
  };

  return { details, fetchDetail };
}
